
import { Options, Vue } from 'vue-class-component';
import ModalBox from '@/shared/components/modal-box/index.vue';
import { Inject, Prop, Watch } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { EventEmitter } from 'events';
import store from '@/trust-admin-app/store';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import V3DatePicker from 'vue3-datepicker';
import AlertBox from '@/shared/components/alert-box/index.vue';
import debounce from 'lodash/debounce';
import { FirmSearchRequest, InvestigationView, LinkedPracticeRequest, LinkedPracticeView, PracticeView } from '@/shared/modules/typescript-trustapi-client/models';


@Options({
  components: {
    ModalBox,
    LoadingIndicator,
    ErrorIndicator,
    V3DatePicker,
    AlertBox
  },
  mixins: [FormatDateTimeMixin]
})
export default class AddEditLinkedPracticeModal extends Vue {

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  // @Inject({ from: 'correspondenceType', default: {} })
  // private correspondenceType!: CorrespondenceType[];

  @Prop({ default: {} })
  public linkedPractice!: LinkedPracticeView;

  @Prop({ default: 1 })
  public mode!: number;

  @Prop({ default: {} })
  public practice!: PracticeView;

  private formData: LinkedPracticeView = null as any;
  private submitting = false;
  private showDeleteAlert = false;

  private isOpen = false;
  private arrowCounter = -1;
  private practiceList: PracticeView[] = [];
  private search = '';
  private isSearching = false;
  private srchDebounced: any = null;
  private currentSelectedPractice: PracticeView = null as any;


  private createInvestigation = false;
  private effectiveDate: Date = null as any;
  private loadingContent = false;
  private errorLoadingContent = false;
  

  @Watch('search')
  private onKeywordChange(): void {
    if (this.search.length > 2 && this.practiceList.filter(x => x.name == this.search).length == 0) {
      this.currentSelectedPractice = null as any;
      setTimeout(this.srchDebounced.cancel, 0);
      setTimeout(this.srchDebounced, 0);
    }
  }

  created(): void {
    this.init();
  }

  mounted(): void {
    window.addEventListener('click', this.handleClickOutside);
    this.srchDebounced = debounce(() => {
      this.searchPractice();
    }, 1000);
  }

  beforeUnmount(): void {
    window.removeEventListener('click', this.handleClickOutside);
  }

  private async init(): Promise<void> {

    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;

      this.formData = JSON.parse(JSON.stringify(this.linkedPractice));
      this.formData.id = this.formData.id ?? 0;
  
      if (this.editMode) {
        this.effectiveDate = this.formData.linkedDate ? new Date(this.formData.linkedDate) : new Date();

        const practiceId = this.mode == 1 ? this.formData.fromPracticeId : this.formData.toPracticeId;

        const resp2: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(
          practiceId
        );
  
        this.currentSelectedPractice = resp2.data.data ?? null;
  
        this.practiceList.push(this.currentSelectedPractice);
        this.search = this.currentSelectedPractice.name;
      }

    } catch {
      this.errorLoadingContent = true;
    } finally {
      this.loadingContent = false;
    }  

  }

  private async searchPractice(): Promise<void> {
    const _body: FirmSearchRequest = {
      keyword: this.search,
      isExactMatch: false
    }
    try {
      this.isSearching = true;

      const resp: any = await this.apiService.PracticeApi?.apiV2PracticeSearchPracticePost(_body);
      this.practiceList = resp.data.data as PracticeView[];
      this.isOpen = this.practiceList.length > 0;
    } catch (err) {
      this.isOpen = false;
    } finally {
      this.isSearching = false;
    }
  }

  private async saveLinkedPractice(): Promise<void> {
    try {

      this.submitting = true;
      const linkedPracReq: LinkedPracticeRequest = {
        id: this.formData.id,
        fromPracticeId: this.mode == 1 ? this.currentSelectedPractice.id : this.practice.id,
        toPracticeId: this.mode == 1 ? this.practice.id : this.currentSelectedPractice.id,
        linkedDate: !this.editMode ? null : this.effectiveDate,
      }
      await this.apiService.PracticeApi?.apiV2PracticeSaveLinkedPracticePost(linkedPracReq);

      if (this.formData.id == 0) {
        this.eventCtrl.emit('show-snackbar', 'success', 'The linked practice has been transfered.');
      } else {
        this.eventCtrl.emit('show-snackbar', 'success', 'The linked practice has been updated');
      }

      if (this.createInvestigation) {
        const investigation: InvestigationView = {
          id: null,
          latestPracticeLogData: {}
        };
        investigation.latestPracticeLogData['practiceId'] = this.mode == 1 ? linkedPracReq.fromPracticeId : linkedPracReq.toPracticeId;

        // console.log(investigation);
        this.eventCtrl.emit('showAddEditInvestigationModal', investigation);
      }

      this.eventCtrl.emit('reload-linked-practices');
      this.closeModal();

    } catch (err) {
      if (this.formData.id == 0) {
        this.eventCtrl.emit('show-snackbar', 'error', 'Problem transfering practice.');
      } else {
        this.eventCtrl.emit('show-snackbar', 'error', 'Problem editing transfer.');
      }
    } finally {
      this.submitting = false;
    }

  }

  private yesClicked(): void {
    this.deleteTransfer();
  }

  private async deleteTransfer(): Promise<void> {
    try {
      this.submitting = true;
      await this.apiService.PracticeApi?.apiV2PracticeDeleteLinkedPracticeIdDelete(
        this.formData.id
      );
      this.eventCtrl.emit('reload-linked-practices');
      this.eventCtrl.emit('show-snackbar', 'success', 'Linked practice has been deleted successfully');
      this.closeModal();
    } catch {
      this.eventCtrl.emit('show-snackbar', 'error', 'Problem deleting linked practice');
    } finally {
      this.submitting = false;
    }
  }

  private selectPractice(p: PracticeView): void {
    this.isOpen = false;
    this.search = p.name;
    this.currentSelectedPractice = p;
  }

  private onArrowDown() {
    if (this.arrowCounter < this.practiceList.length) {
      this.arrowCounter = this.arrowCounter + 1;
    }
  }

  private onArrowUp(): void {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    }
  }

  private onEnter() {
    if (this.practiceList.length > 0) {
      this.selectPractice(
        this.practiceList[this.arrowCounter === -1 ? 0 : this.arrowCounter]
      );
    }
    this.isOpen = false;
  }

  private handleClickOutside(evt: any): void {
    if (this.$el === (evt.target)) {
      this.isOpen = false;
      this.arrowCounter = -1;
    }
  }

  private highlightWord(word: string): string {
    try {
      const regex = new RegExp('(' + this.search + ')', 'ig');
      return word.replace(regex, '<mark>$1</mark>');
    } catch (err) {
      return word;
    }
  }



  get validForm(): boolean {
    if (this.editMode) {
      return !!this.currentSelectedPractice && !!this.effectiveDate;
    } else {
      return !!this.currentSelectedPractice;
    }
  }

  get isFormDirty(): boolean {
    // const chk1 = this.currentSelectedPractice.id != (this.mode == 1 ? this.formData.fromPracticeId : this.formData.toPracticeId);
    // const chk2 = moment(moment(this.formData.linkedDate ?? this.effectiveDate).format('YYYY-MM-DD')).unix() !=
    //     moment(moment(this.effectiveDate).format('YYYY-MM-DD')).unix();

    // return chk1 && chk2;
    return true;
  }

  get practiceName(): string {
    return this.practice?.branchId > 0 ? (this.practice.firmName + ' (' + this.practice.branchTradingName + ')') : this.practice.firmName;
  }

  get editMode(): boolean {
    return this.linkedPractice.id > 0;
  }

  private closeModal() {
    this.$emit('closeAddEditLinkedPracticeModal');
  }


  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

}

